// The assistant's brief at the top of a space (Stands, Risk, Next), a track (a paragraph) and an activity (what it
// knows). Shown at once from what is stored; when something changed since, it says "Updating" and the fresh one
// fades in when it arrives. AI is violet: the panel has the faint violet tint and the orb.
import type { ReactNode } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import type { BriefKind } from '@dpw/shared';
import { useBrief } from '../lib/queries';
import { ago } from '../lib/format';
import { duration, ease } from '../lib/motion';
import { Orb, Skel } from './motion';

const TITLE: Record<BriefKind, string> = { space: 'Space brief', track: 'Track brief', activity: 'What the AI knows about this', day: 'Your day' };

export function BriefPanel({ kind, id, children, className = '' }: { kind: BriefKind; id: string; children?: ReactNode; className?: string }) {
  const q = useBrief(kind, id);
  const b = q.data;
  return (
    <section className={`brief ${kind} ${className}`} aria-label={TITLE[kind]} aria-busy={q.isLoading || b?.stale}>
      <div className="bh">
        <span className="k"><Orb size={9} state={b?.stale ? 'thinking' : 'idle'} />{TITLE[kind]}</span>
        <span className="xs mut">{!b ? '' : b.stale ? 'Updating' : kind === 'track' ? `Updated ${ago(b.generatedAt)} from ${b.basis}` : `Updated ${ago(b.generatedAt)}`}</span>
      </div>
      {q.isLoading ? (
        <div className="col gap6" role="status" aria-label="Loading"><Skel w="90%" h={12} /><Skel w="75%" h={12} /><Skel w="60%" h={12} /></div>
      ) : q.error ? <div className="xs mut">The brief could not be loaded. Everything below is live.</div> : b && (
        <AnimatePresence mode="wait" initial={false}>
          <motion.div key={b.generatedAt} initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { duration: duration.base, ease } }} exit={{ opacity: 0, transition: { duration: duration.fast } }}>
            {/* A space brief has both: the page shows its lines, the phone its paragraph. */}
            {b.paragraph && !b.lines.length && <p className="bp">{b.paragraph}</p>}
            {b.lines.length > 0 && (
              <div className="bl2">
                {b.lines.map(l => <div className="brow" key={l.label}><span className={`bk${l.tone ? ` ${l.tone}` : ''}`}>{l.label}</span><span className="bv">{l.text}</span></div>)}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      )}
      {kind === 'activity' && b && <div className="xs mut bfrom">From {b.basis}</div>}
      {children}
    </section>
  );
}
